import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Eye,
  Edit3,
  Trash2,
  Instagram,
  Facebook,
  Twitter,
  Twitter as XIcon,
  Linkedin,
  Globe,
  Mail,
  Loader2,
  Send,
  ImageIcon,
  X,
  Save,
  RefreshCw,
  ArrowRight,
  Upload,
  Sparkles,
  Search,
  MessageSquare,
  Wand2,
  Check,
  Copy,
  Link,
  ExternalLink,
  CalendarDays,
  Phone,
  Pin,
  Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import GuidedProjectLayout, { useProjectSteps } from '@/components/ios26/GuidedProjectLayout';
import { GlassCard, PillButton, SectionHeader, StatusChip } from '@/components/ios26/IOSComponents';
import '@/styles/ios26-design-system.css';

/**
 * Platform Icons Map
 */
const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  x: Twitter,
  x_twitter: Twitter,
  twitter: Twitter,
  linkedin: Linkedin,
  telegram: Send,
  whatsapp: Phone,
  // iter 242 P0 — Pinterest must never fall back to Globe. lucide-react
  // does not ship a Pinterest icon; ``Pin`` is the closest brand-neutral
  // representation and clearly distinguishes Pinterest from "blog".
  pinterest: Pin,
  blog: Globe,
  email: Mail,
  brevo: Mail
};

const PLATFORM_COLORS = {
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
  facebook: 'bg-blue-600',
  x: 'bg-gray-900',
  x_twitter: 'bg-gray-900',
  twitter: 'bg-gray-900',
  linkedin: 'bg-blue-700',
  telegram: 'bg-sky-500',
  whatsapp: 'bg-green-500',
  pinterest: 'bg-red-600',
  blog: 'bg-emerald-500',
  email: 'bg-amber-500',
  brevo: 'bg-amber-500'
};

const PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  x: 'X (Twitter)',
  x_twitter: 'X (Twitter)',
  twitter: 'X (Twitter)',
  linkedin: 'LinkedIn',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  blog: 'Blog',
  email: 'Email',
  brevo: 'Email (Brevo)'
};

// Status configurations with distinct colors
const STATUS_CONFIG = {
  draft: { 
    color: 'bg-amber-100 text-amber-800 border-amber-300', 
    label: 'Brouillon',
    icon: Edit3,
    dotColor: 'bg-amber-500'
  },
  validated: { 
    color: 'bg-blue-100 text-blue-800 border-blue-300', 
    label: 'Validé',
    icon: Check,
    dotColor: 'bg-blue-500'
  },
  scheduled: { 
    color: 'bg-purple-100 text-purple-800 border-purple-300', 
    label: 'Planifié',
    icon: Clock,
    dotColor: 'bg-purple-500'
  },
  published: { 
    color: 'bg-green-100 text-green-800 border-green-300', 
    label: 'Publié',
    icon: CheckCircle,
    dotColor: 'bg-green-500'
  },
  failed: { 
    color: 'bg-red-100 text-red-800 border-red-300', 
    label: 'Échec',
    icon: AlertCircle,
    dotColor: 'bg-red-500'
  }
};

// P0 FIX: Canonical platform normalization (mirrors backend
// platform_constants.normalize_platform_id). Any legacy 'x'/'twitter'/
// 'x-twitter' value must resolve to the single canonical 'x_twitter' id.
function normalizePlatformId(platformId) {
  if (!platformId) return platformId;
  const p = String(platformId).toLowerCase().trim();
  if (['x', 'twitter', 'x-twitter', 'x_twitter'].includes(p)) return 'x_twitter';
  return p;
}

// P0 FIX: Canonical content validation status — single source of truth,
// NEVER platform-dependent. `validation_flag` is the field actually
// persisted by every content-creation path; `status` is a legacy/derived
// field that is missing on some documents (e.g. native X/Twitter content).
// Every read of a content's status MUST go through this helper so the
// "Valider" action is never hidden because of a missing/stale field.
function getContentStatus(content) {
  if (!content) return 'draft';
  if (content.published === true) return 'published';
  return content.validation_flag || content.status || 'draft';
}

/**
 * AI Assistant Component for text/image editing
 */
function AIAssistant({ onApplyText, onSearchImage, currentText }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('text');
  
  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    
    try {
      if (mode === 'text') {
        const response = await apiClient.post('/ai/improve-text', {
          original_text: currentText,
          instruction: prompt
        });
        if (response.data?.improved_text) {
          onApplyText(response.data.improved_text);
          toast.success('Texte mis à jour');
        }
      } else {
        onSearchImage(prompt);
      }
      setPrompt('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-[hsl(var(--color-surface-2))] rounded-xl p-4 border border-[hsl(var(--color-border))]">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-indigo-500" />
        <span className="font-medium text-[hsl(var(--color-text))]">Assistant IA</span>
      </div>
      
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setMode('text')}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
            mode === 'text' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-[hsl(var(--color-surface))] text-[hsl(var(--color-text-2))] hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
          )}
        >
          <MessageSquare className="w-4 h-4 inline mr-1" />
          Texte
        </button>
        <button
          onClick={() => setMode('image')}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
            mode === 'image' 
              ? 'bg-indigo-600 text-white' 
              : 'bg-[hsl(var(--color-surface))] text-[hsl(var(--color-text-2))] hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
          )}
        >
          <Search className="w-4 h-4 inline mr-1" />
          Image
        </button>
      </div>
      
      <div className="flex gap-2">
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder={mode === 'text' 
            ? 'Ex: Rends le texte plus engageant...' 
            : 'Ex: Bureau moderne avec plantes...'}
          className="flex-1 px-3 py-2 bg-[hsl(var(--color-surface))] border border-[hsl(var(--color-border))] rounded-lg text-sm text-[hsl(var(--color-text))] placeholder:text-[hsl(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          onClick={handleSubmit}
          disabled={loading || !prompt.trim()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Image Search Results from Unsplash/Pexels
 */
function ImageSearchResults({ images, onSelect, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Recherche en cours...</span>
      </div>
    );
  }
  
  if (!images || images.length === 0) return null;
  
  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-gray-700 mb-2">Résultats ({images.length})</h4>
      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
        {images.map((img, index) => (
          <button
            key={index}
            onClick={() => onSelect(img.url)}
            className="relative aspect-square rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all"
          >
            <img src={img.thumbnail || img.url} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Duplicate to Platforms Modal
 */
function DuplicateModal({ content, project, onClose, onDuplicate }) {
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [adaptContent, setAdaptContent] = useState(true);
  const [ctaUrl, setCtaUrl] = useState(project?.site_url || '');
  const [ctaText, setCtaText] = useState('En savoir plus');
  const [duplicating, setDuplicating] = useState(false);
  
  const availablePlatforms = Object.keys(PLATFORM_LABELS).filter(
    p => p !== content.platform && p !== 'twitter' // twitter is alias for x
  );
  
  const connectedPlatforms = Object.keys(project?.channels_config || {}).filter(
    p => project.channels_config[p]?.connected && !project.channels_config[p]?.reauth_required
  );
  
  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };
  
  const handleDuplicate = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Sélectionnez au moins une plateforme');
      return;
    }
    
    setDuplicating(true);
    try {
      const response = await apiClient.post(`/contents/${content.id}/duplicate`, {
        target_platforms: selectedPlatforms,
        adapt_content: adaptContent,
        cta_url: ctaUrl,
        cta_text: ctaText
      });
      
      if (response.data?.success) {
        toast.success(response.data.message);
        onDuplicate(response.data.duplicated);
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    } finally {
      setDuplicating(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Copy className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ color: 'hsl(var(--color-text))' }}>Dupliquer vers</h2>
              <p className="text-sm text-gray-500">Adaptez automatiquement à chaque plateforme</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              Plateformes cibles
            </label>
            <div className="grid grid-cols-3 gap-2">
              {availablePlatforms.map(platform => {
                const Icon = PLATFORM_ICONS[platform];
                const isConnected = connectedPlatforms.includes(platform) || ['email', 'brevo', 'blog'].includes(platform);
                const isSelected = selectedPlatforms.includes(platform);
                
                return (
                  <button
                    key={platform}
                    onClick={() => isConnected && togglePlatform(platform)}
                    disabled={!isConnected}
                    className={cn(
                      'p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1',
                      isSelected && 'border-indigo-500 bg-indigo-50',
                      !isSelected && isConnected && 'border-gray-200 hover:border-indigo-300',
                      !isConnected && 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    )}
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center text-white',
                      PLATFORM_COLORS[platform] || 'bg-gray-500'
                    )}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">
                      {PLATFORM_LABELS[platform]?.split(' ')[0]}
                    </span>
                    {!isConnected && (
                      <span className="text-xs text-gray-400">Non connecté</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Adapt Content Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <span className="font-medium text-gray-800">Adapter le contenu</span>
              <p className="text-xs text-gray-500">L&apos;IA adapte le texte aux contraintes de chaque plateforme</p>
            </div>
            <button
              onClick={() => setAdaptContent(!adaptContent)}
              className={cn(
                'w-12 h-6 rounded-full transition-colors',
                adaptContent ? 'bg-indigo-600' : 'bg-gray-300'
              )}
            >
              <div className={cn(
                'w-5 h-5 rounded-full bg-white shadow transition-transform',
                adaptContent ? 'translate-x-6' : 'translate-x-0.5'
              )} />
            </button>
          </div>
          
          {/* CTA Configuration */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800">
              <Link className="w-4 h-4 inline mr-2" />
              Call to Action
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  type="text"
                  value={ctaText}
                  onChange={e => setCtaText(e.target.value)}
                  placeholder="Texte du CTA"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <input
                  type="url"
                  value={ctaUrl}
                  onChange={e => setCtaUrl(e.target.value)}
                  placeholder="URL de destination"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <PillButton variant="secondary" onClick={onClose}>
            Annuler
          </PillButton>
          <PillButton
            variant="primary"
            onClick={handleDuplicate}
            disabled={duplicating || selectedPlatforms.length === 0}
            icon={duplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
          >
            Dupliquer ({selectedPlatforms.length})
          </PillButton>
        </div>
      </div>
    </div>
  );
}

/**
 * Content Editor Modal - Improved with CTA and duplicate features
 */
function ContentEditorModal({ content, project, onClose, onSave, onDelete, onPublish, onRefresh }) {
  const navigate = useNavigate();
  const [editedContent, setEditedContent] = useState({
    text: content?.text || '',
    scheduled_date: content?.scheduled_date ? content.scheduled_date.split('T')[0] : '',
    scheduled_time: content?.scheduled_date ? content.scheduled_date.split('T')[1]?.substring(0, 5) || '09:00' : '09:00',
    hashtags: content?.hashtags || [],
    image_url: content?.image_url || '',
    cta_url: content?.cta_url || project?.site_url || '',
    cta_text: content?.cta_text || 'En savoir plus',
    // P0 FIX: Support X variant
    x_variant_text: content?.variants?.x_twitter?.text || ''
  });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [retryingBrevoSync, setRetryingBrevoSync] = useState(false);
  const [searchImages, setSearchImages] = useState([]);
  const [searchingImages, setSearchingImages] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const fileInputRef = useRef(null);
  
  if (!content) return null;
  
  const Icon = PLATFORM_ICONS[content.platform] || Globe;
  const status = STATUS_CONFIG[getContentStatus(content)] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  
  // P0 FIX: Check if X integration is connected
  const isXPlatform = normalizePlatformId(content.platform) === 'x_twitter';
  const xConfig = project?.channels_config?.x_twitter || project?.channels_config?.x || {};
  const isXConnected = xConfig.connected === true;
  const xNeedsReauth = xConfig.reauth_required === true || xConfig.token_invalid === true;
  
  // P0 FIX CAMPAGNE X: For X native content, use text directly (not variant)
  // For non-X content being duplicated to X, use variant
  const isXNativeContent = isXPlatform; // Content created specifically for X
  
  // P0 FIX: Get text for X
  // - For X native content: use editedContent.text directly
  // - For other content with X variant: use x_variant_text
  const xText = isXNativeContent 
    ? editedContent.text 
    : (editedContent.x_variant_text || editedContent.text);
  const xCharCount = xText?.length || 0;
  const xTooLong = xCharCount > 280;
  const xTextEmpty = !xText || !xText.trim();
  
  // P0 FIX: Determine if publish is blocked
  const publishBlocked = isXPlatform && (!isXConnected || xNeedsReauth || xTooLong || xTextEmpty);
  const publishBlockedReason = xTextEmpty ? 'Contenu vide' :
    !isXConnected ? 'X non connecté' : 
    xNeedsReauth ? 'Session X expirée' : 
    xTooLong ? `Tweet trop long (${xCharCount}/280)` : '';
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const scheduledDateTime = `${editedContent.scheduled_date}T${editedContent.scheduled_time}:00`;
      
      // P0 FIX CAMPAGNE X: For X native content, do NOT use variants
      // The main text IS the tweet. For other platforms duplicated to X, use variants.
      const variants = {};
      if (!isXNativeContent && editedContent.x_variant_text && editedContent.x_variant_text !== editedContent.text) {
        // Only add X variant for non-X content
        variants.x_twitter = {
          text: editedContent.x_variant_text,
          char_count: editedContent.x_variant_text.length
        };
      }
      
      await onSave({
        ...content,
        text: editedContent.text,
        scheduled_date: scheduledDateTime,
        hashtags: editedContent.hashtags,
        image_url: editedContent.image_url,
        cta_url: editedContent.cta_url,
        cta_text: editedContent.cta_text,
        // P0 FIX: Only include variants if we have them (non-X content)
        variants: Object.keys(variants).length > 0 ? variants : undefined
      });
      toast.success('Contenu mis à jour');
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleValidate = async () => {
    setSaving(true);
    try {
      const scheduledDateTime = `${editedContent.scheduled_date}T${editedContent.scheduled_time}:00`;
      
      // P0 FIX CAMPAGNE X: Same logic as handleSave
      const variants = {};
      if (!isXNativeContent && editedContent.x_variant_text && editedContent.x_variant_text !== editedContent.text) {
        variants.x_twitter = {
          text: editedContent.x_variant_text,
          char_count: editedContent.x_variant_text.length
        };
      }
      
      await onSave({
        ...content,
        text: editedContent.text,
        scheduled_date: scheduledDateTime,
        hashtags: editedContent.hashtags,
        image_url: editedContent.image_url,
        cta_url: editedContent.cta_url,
        cta_text: editedContent.cta_text,
        status: 'validated',
        validation_flag: 'validated',
        variants: Object.keys(variants).length > 0 ? variants : undefined
      });
      toast.success('Article validé !');
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };
  
  // P0.4 FIX: Reset failed content back to validated status
  const handleResetToValidated = async () => {
    setSaving(true);
    try {
      await onSave({
        ...content,
        status: 'validated',
        validation_flag: 'validated',
        published: false,
        publish_error: null,
        publish_attempted_at: null
      });
      toast.success('Contenu remis en "Validé" - Vous pouvez le reprogrammer ou le publier à nouveau');
      onRefresh && onRefresh();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la remise en état');
    } finally {
      setSaving(false);
    }
  };
  
  // P0 FIX: handlePublish must use POST, never GET
  // Added e.preventDefault() and e.stopPropagation() to prevent any form submission or navigation
  const handlePublish = async (e) => {
    // Prevent any default behavior (form submit, link navigation, etc.)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setPublishing(true);
    try {
      console.info('[PUBLISH] handlePublish triggered via button click');
      const result = await onPublish(content);
      if (result?.success) {
        // P0 FIX: Show detailed success message based on media attachment status
        if (result.tweet_attached_media && result.media_id) {
          toast.success(
            <div>
              <strong>✅ Tweet publié avec image</strong>
              <br />
              <span className="text-xs opacity-70">ID: {result.tweet_id}</span>
              {result.media_id && <span className="text-xs opacity-70 block">📸 Media ID: {result.media_id}</span>}
            </div>,
            { duration: 5000 }
          );
        } else if (result.image_requested === false) {
          // No image was requested - text-only tweet
          toast.success(result.message || 'Publication réussie !');
        } else {
          // Image was requested but we don't have tweet_attached_media info
          toast.success(result.message || 'Publication réussie !');
        }
        
        // Log detailed response for debugging
        console.info('[PUBLISH] Success response:', {
          tweet_id: result.tweet_id,
          tweet_attached_media: result.tweet_attached_media,
          media_id: result.media_id,
          image_url_used: result.image_url_used,
          image_source: result.image_source
        });
      } else {
        toast.success('Publication en cours...');
      }
      onClose();
    } catch (error) {
      // iter 247 P0 — never collapse a structured backend error into a
      // blind default. Priority: response.data.message ->
      // response.data.error.message -> response.data.detail.message ->
      // error.message -> generic fallback only as a last resort.
      const message = error.response?.data?.message
        || error.response?.data?.error?.message
        || error.response?.data?.detail?.message
        || (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : null)
        || error.message
        || 'Erreur lors de la publication';
      toast.error(message);
    } finally {
      setPublishing(false);
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce contenu ?')) return;
    setDeleting(true);
    try {
      await onDelete(content);
      toast.success('Contenu supprimé');
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };
  
  // Copy content for WhatsApp or manual publishing
  const handleCopyContent = async () => {
    try {
      let textToCopy = editedContent.text || content.text;
      
      // Add hashtags if present
      const hashtags = editedContent.hashtags || content.hashtags || [];
      if (hashtags.length > 0) {
        textToCopy += '\n\n' + hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ');
      }
      
      // Add CTA if present
      if (editedContent.cta_url || content.cta_url) {
        textToCopy += '\n\n🔗 ' + (editedContent.cta_url || content.cta_url);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      
      // Check if there's an image
      const imageUrl = editedContent.image_url || content.image_url;
      if (imageUrl && imageUrl.startsWith('http')) {
        // Open image in new tab
        window.open(imageUrl, '_blank');
        toast.success(
          <div>
            <strong>📋 Texte copié !</strong>
            <p className="text-sm mt-1">
              1. Téléchargez l'image ouverte<br/>
              2. Dans WhatsApp : partagez l'image<br/>
              3. Collez le texte dans le message
            </p>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.success('📋 Texte copié ! Collez-le dans WhatsApp');
      }
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };
  
  const handleRegenerateImage = async () => {
    setRegeneratingImage(true);
    try {
      // iter250 P0 fix: Gemini image generation regularly takes 15-20s+
      // (confirmed via live test), close to the default 30s axios
      // timeout — bump it here specifically instead of globally.
      const response = await apiClient.post(
        `/contents/${content.id}/regenerate-image`, {}, { timeout: 60000 }
      );
      if (response.data?.image_url) {
        setEditedContent(prev => ({ ...prev, image_url: response.data.image_url }));
        toast.success('Nouvelle image générée');
      }
    } catch (error) {
      // iter250 P0 fix: surface the real provider/backend error instead
      // of a blind generic message (same pattern as iter247 publish fix).
      const message = error.response?.data?.detail
        || error.response?.data?.message
        || (error.code === 'ECONNABORTED' ? "Génération de l'image trop longue, réessayez" : null)
        || error.message
        || 'Erreur lors de la génération';
      toast.error(message);
    } finally {
      setRegeneratingImage(false);
    }
  };
  
  const handleRetryBrevoSync = async () => {
    setRetryingBrevoSync(true);
    try {
      const response = await apiClient.post(
        `/email-growth/${content.id}/sync-brevo`, {}, { timeout: 30000 }
      );
      toast.success('Synchronisation Brevo réussie');
      if (response.data?.content) {
        setEditedContent(prev => ({ ...prev, ...response.data.content }));
      }
      onRefresh?.();
    } catch (error) {
      const message = error.response?.data?.detail?.message
        || error.response?.data?.message
        || error.message
        || 'La synchronisation Brevo a échoué';
      toast.error(message);
    } finally {
      setRetryingBrevoSync(false);
    }
  };

  const handleSearchImage = async (query) => {
    setSearchingImages(true);
    try {
      const response = await apiClient.get(`/images/search?query=${encodeURIComponent(query)}`);
      setSearchImages(response.data?.images || []);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setSearchingImages(false);
    }
  };
  
  const handleSelectImage = (imageUrl) => {
    setEditedContent(prev => ({ ...prev, image_url: imageUrl }));
    setSearchImages([]);
    setShowImageOptions(false);
    toast.success('Image sélectionnée');
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Don't set Content-Type - let axios set it with the proper boundary
      const response = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': undefined }
      });
      if (response.data?.url) {
        setEditedContent(prev => ({ ...prev, image_url: response.data.url }));
        toast.success('Image uploadée');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    }
    setShowImageOptions(false);
  };
  
  const handleApplyAIText = (newText) => {
    setEditedContent(prev => ({ ...prev, text: newText }));
  };
  
  const handleDuplicateSuccess = () => {
    onRefresh?.();
  };
  
  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" 
        onClick={onClose}
      >
        <div 
          className="bg-[hsl(var(--color-surface))] rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-[hsl(var(--color-border))] bg-[hsl(var(--color-surface))] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg',
                PLATFORM_COLORS[content.platform] || 'bg-gray-500'
              )}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-[hsl(var(--color-text))]">{PLATFORM_LABELS[content.platform]}</h2>
                <span className={cn(
                  'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border',
                  status.color
                )}>
                  <StatusIcon className="w-3 h-3" />
                  {status.label}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowDuplicateModal(true)}
                className="p-2.5 hover:bg-indigo-500/10 rounded-xl transition-colors text-indigo-500"
                title="Dupliquer vers d'autres plateformes"
              >
                <Copy className="w-5 h-5" />
              </button>
              <button 
                onClick={onClose} 
                className="p-2.5 hover:bg-[hsl(var(--color-surface-2))] rounded-xl transition-colors text-[hsl(var(--color-text-2))]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* P0 UX: Error Details Panel for failed content */}
          {getContentStatus(content) === 'failed' && content.publish_error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-red-800 mb-1">
                    {content.publish_error.user_message || 'Échec de la publication'}
                  </h3>
                  <p className="text-sm text-red-700 mb-3">
                    {content.publish_error.action_hint || 'Vérifiez la configuration et réessayez.'}
                  </p>
                  
                  {/* Action buttons based on error type */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {content.publish_error.retryable && (
                      <button
                        onClick={handleResetToValidated}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Réessayer
                      </button>
                    )}
                    {content.publish_error.action_type === 'reconnect' && (
                      <button
                        onClick={() => navigate('/project/settings?tab=integrations')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Reconnecter le compte
                      </button>
                    )}
                    {content.publish_error.action_type === 'settings' && (
                      <button
                        onClick={() => navigate('/project/settings?tab=integrations')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-500 text-white text-xs font-medium rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Ouvrir les paramètres
                      </button>
                    )}
                  </div>
                  
                  {/* Technical details (collapsible) */}
                  {content.publish_error.error_details && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-red-600 hover:text-red-700 font-medium">
                        Voir les détails techniques
                      </summary>
                      <div className="mt-2 p-2 bg-red-100 rounded-lg font-mono text-red-800 overflow-x-auto">
                        <div><strong>Étape:</strong> {content.publish_error.error_details.stage || 'N/A'}</div>
                        {content.publish_error.error_details.http_status && (
                          <div><strong>HTTP:</strong> {content.publish_error.error_details.http_status}</div>
                        )}
                        {content.publish_error.error_details.error_code && (
                          <div><strong>Code:</strong> {content.publish_error.error_details.error_code}</div>
                        )}
                        {content.publish_error.error_details.raw_message && (
                          <div><strong>Message:</strong> {content.publish_error.error_details.raw_message.substring(0, 200)}</div>
                        )}
                        {content.publish_error.error_details.trace_id && (
                          <div><strong>Trace ID:</strong> {content.publish_error.error_details.trace_id}</div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Simple error message fallback for content without publish_error object */}
          {getContentStatus(content) === 'failed' && !content.publish_error && content.error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-800 mb-1">Échec de la publication</h3>
                  <p className="text-sm text-red-700">{content.error}</p>
                </div>
              </div>
            </div>
          )}

          {/* iter 251 P0 — Brevo sync failure banner: the email itself is
              preserved (never lost), only the Brevo campaign creation
              failed. Dedicated idempotent retry, never re-runs GPT. */}
          {content.brevo_sync_error && (
            <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="brevo-sync-error-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-amber-800 mb-1">Synchronisation Brevo échouée</h3>
                  <p className="text-sm text-amber-700 mb-3 font-mono">
                    {content.brevo_sync_error.message || content.brevo_sync_error.error_code}
                  </p>
                  <button
                    onClick={handleRetryBrevoSync}
                    disabled={retryingBrevoSync}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                    data-testid="retry-brevo-sync-button"
                  >
                    {retryingBrevoSync ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Réessayer la synchronisation Brevo
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6 bg-[hsl(var(--color-surface))]">
            {/* Image Section */}
            <div className="relative">
              {editedContent.image_url ? (
                <div className="relative rounded-2xl overflow-hidden bg-gray-50 border border-gray-200">
                  <img 
                    src={editedContent.image_url} 
                    alt="Visuel" 
                    className="w-full h-56 object-cover"
                  />
                  <div className="absolute top-3 right-3 flex gap-2">
                    <button 
                      onClick={handleRegenerateImage}
                      disabled={regeneratingImage}
                      className="p-2.5 bg-white hover:bg-gray-50 rounded-xl shadow-lg transition-all disabled:opacity-50 group"
                      title="Régénérer l'image"
                    >
                      {regeneratingImage 
                        ? <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                        : <RefreshCw className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" />
                      }
                    </button>
                    <button 
                      onClick={() => setShowImageOptions(!showImageOptions)}
                      className="p-2.5 bg-white hover:bg-gray-50 rounded-xl shadow-lg transition-all group"
                      title="Changer l'image"
                    >
                      <ImageIcon className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" />
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="h-40 bg-[hsl(var(--color-surface-2))] border-2 border-dashed border-[hsl(var(--color-border))] rounded-2xl flex items-center justify-center cursor-pointer hover:border-indigo-400 transition-colors"
                  onClick={() => setShowImageOptions(true)}
                >
                  <div className="text-center">
                    <ImageIcon className="w-10 h-10 text-[hsl(var(--color-text-muted))] mx-auto mb-2" />
                    <p className="text-sm text-[hsl(var(--color-text-muted))]">Ajouter une image</p>
                  </div>
                </div>
              )}
              
              {/* Image Options Dropdown */}
              {showImageOptions && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[hsl(var(--color-surface))] rounded-xl shadow-xl border border-[hsl(var(--color-border))] p-4 z-10">
                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[hsl(var(--color-surface-2))] hover:bg-[hsl(var(--color-surface-3))] rounded-xl transition-colors"
                    >
                      <Upload className="w-5 h-5 text-[hsl(var(--color-text-2))]" />
                      <span className="font-medium text-[hsl(var(--color-text))]">Uploader</span>
                    </button>
                    <button
                      onClick={handleRegenerateImage}
                      disabled={regeneratingImage}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {regeneratingImage 
                        ? <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                        : <Sparkles className="w-5 h-5 text-indigo-500" />
                      }
                      <span className="font-medium text-indigo-500">Générer</span>
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  <ImageSearchResults 
                    images={searchImages} 
                    onSelect={handleSelectImage}
                    loading={searchingImages}
                  />
                </div>
              )}
            </div>
            
            {/* Text Content - With X counter for X native content */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-[hsl(var(--color-text))]">
                  {isXNativeContent ? 'Tweet' : 'Texte du post'}
                </label>
                {/* P0 FIX CAMPAGNE X: Show 280 counter for X native content */}
                {isXNativeContent && (
                  <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    xCharCount > 280 
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' 
                      : xCharCount > 250 
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                  }`}>
                    <XIcon className="w-3 h-3" />
                    {xCharCount} / 280
                  </span>
                )}
              </div>
              <textarea
                value={editedContent.text}
                onChange={e => setEditedContent(prev => ({ ...prev, text: e.target.value }))}
                rows={isXNativeContent ? 4 : 6}
                maxLength={isXNativeContent ? 280 : undefined}
                className={`w-full px-4 py-3 bg-[hsl(var(--color-surface))] border rounded-xl text-[hsl(var(--color-text))] placeholder:text-[hsl(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none ${
                  isXNativeContent && xCharCount > 280 
                    ? 'border-red-500' 
                    : 'border-[hsl(var(--color-border))]'
                }`}
                placeholder={isXNativeContent ? "Votre tweet (280 caractères max)..." : "Contenu de votre publication..."}
              />
              {/* P0 FIX CAMPAGNE X: Show error for X if too long or empty */}
              {isXNativeContent && xCharCount > 280 && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Le tweet dépasse 280 caractères. Raccourcissez le contenu pour publier.
                </p>
              )}
              {isXNativeContent && xTextEmpty && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Le contenu du tweet ne peut pas être vide.
                </p>
              )}
              {isXNativeContent && !xTooLong && !xTextEmpty && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  💡 Conseil : 1 idée forte, 0-2 hashtags, ton direct.
                </p>
              )}
              {!isXNativeContent && (
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-[hsl(var(--color-text-muted))]">
                    {editedContent.text.length} caractères
                  </span>
                </div>
              )}
            </div>
            
            {/* P0 FIX CAMPAGNE X: X Variant Section - ONLY show for NON-X content 
                (when duplicating other content to X, user can add a X-specific variant) */}
            {isXPlatform && !isXNativeContent && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-blue-800 dark:text-blue-200">
                    <XIcon className="w-4 h-4" />
                    Variante X (Twitter)
                  </label>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    xCharCount > 280 
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' 
                      : xCharCount > 250 
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                  }`}>
                    {xCharCount} / 280
                  </span>
                </div>
                <textarea
                  value={editedContent.x_variant_text || editedContent.text}
                  onChange={e => setEditedContent(prev => ({ ...prev, x_variant_text: e.target.value }))}
                  rows={4}
                  maxLength={280}
                  className={`w-full px-4 py-3 bg-white dark:bg-gray-900 border rounded-xl text-[hsl(var(--color-text))] placeholder:text-[hsl(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    xCharCount > 280 ? 'border-red-500' : 'border-blue-200 dark:border-blue-700'
                  }`}
                  placeholder="Version courte pour X (280 car. max)..."
                />
                {xCharCount > 280 && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Le tweet dépasse 280 caractères. Raccourcissez le contenu pour publier.
                  </p>
                )}
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  💡 Conseil : 1 idée forte, 0-2 hashtags, ton direct.
                </p>
              </div>
            )}
            
            {/* AI Assistant */}
            <AIAssistant 
              currentText={editedContent.text}
              onApplyText={handleApplyAIText}
              onSearchImage={handleSearchImage}
            />
            
            {/* CTA Section */}
            <div className="bg-[hsl(var(--color-surface-2))] rounded-xl p-4 border border-[hsl(var(--color-border))]">
              <label className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--color-text))] mb-3">
                <ExternalLink className="w-4 h-4" />
                Call to Action (CTA)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[hsl(var(--color-text-muted))] mb-1">Texte du bouton</label>
                  <input
                    type="text"
                    value={editedContent.cta_text}
                    onChange={e => setEditedContent(prev => ({ ...prev, cta_text: e.target.value }))}
                    placeholder="En savoir plus"
                    className="w-full px-3 py-2 bg-[hsl(var(--color-surface))] border border-[hsl(var(--color-border))] rounded-lg text-sm text-[hsl(var(--color-text))] placeholder:text-[hsl(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[hsl(var(--color-text-muted))] mb-1">URL de destination</label>
                  <input
                    type="url"
                    value={editedContent.cta_url}
                    onChange={e => setEditedContent(prev => ({ ...prev, cta_url: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-[hsl(var(--color-surface))] border border-[hsl(var(--color-border))] rounded-lg text-sm text-[hsl(var(--color-text))] placeholder:text-[hsl(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
            
            {/* Hashtags */}
            {editedContent.hashtags.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-[hsl(var(--color-text))] mb-2">
                  Hashtags
                </label>
                <div className="flex flex-wrap gap-2">
                  {editedContent.hashtags.map((tag, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1.5 bg-indigo-500/10 text-indigo-500 text-sm rounded-full border border-indigo-500/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[hsl(var(--color-text))] mb-2">
                  {content.published ? 'Date de publication' : 'Date prévue'}
                </label>
                {content.published && content.published_at ? (
                  <div className="w-full px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl text-green-600 font-medium">
                    ✅ {new Date(content.published_at).toLocaleDateString('fr-FR', { 
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </div>
                ) : (
                  <input
                    type="date"
                    value={editedContent.scheduled_date}
                    onChange={e => setEditedContent(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    className="w-full px-4 py-3 bg-[hsl(var(--color-surface))] border border-[hsl(var(--color-border))] rounded-xl text-[hsl(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[hsl(var(--color-text))] mb-2">
                  {content.published ? 'Heure de publication' : 'Heure prévue'}
                </label>
                {content.published && content.published_at ? (
                  <div className="w-full px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl text-green-600 font-medium">
                    🕐 {new Date(content.published_at).toLocaleTimeString('fr-FR', {
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </div>
                ) : (
                  <input
                    type="time"
                    value={editedContent.scheduled_time}
                    onChange={e => setEditedContent(prev => ({ ...prev, scheduled_time: e.target.value }))}
                    className="w-full px-4 py-3 bg-[hsl(var(--color-surface))] border border-[hsl(var(--color-border))] rounded-xl text-[hsl(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="p-6 border-t border-[hsl(var(--color-border))] bg-[hsl(var(--color-surface-2))] flex items-center justify-between gap-3">
            <button
              onClick={handleDelete}
              disabled={deleting || getContentStatus(content) === 'published'}
              className="flex items-center gap-2 px-4 py-2.5 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors disabled:opacity-50 font-medium"
              data-testid="content-delete-button"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Supprimer
            </button>
            
            <div className="flex items-center gap-3">
              {/* Copy button for WhatsApp and manual platforms */}
              {['whatsapp', 'telegram'].includes(content.platform) && (
                <PillButton
                  variant="secondary"
                  onClick={handleCopyContent}
                  icon={<Copy className="w-4 h-4" />}
                >
                  Copier
                </PillButton>
              )}
              
              {/* P0 FIX: draft/brouillon must always offer "Valider", on
                  every platform including x_twitter — never gated by
                  content.platform. Uses canonical getContentStatus(). */}
              {getContentStatus(content) === 'draft' && (
                <PillButton
                  variant="secondary"
                  onClick={handleValidate}
                  disabled={saving}
                  icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  data-testid="content-validate-button"
                >
                  Valider
                </PillButton>
              )}
              
              {/* P0.4 FIX: Retry button for failed content */}
              {getContentStatus(content) === 'failed' && (
                <PillButton
                  variant="secondary"
                  onClick={handleResetToValidated}
                  disabled={saving}
                  icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                >
                  Remettre en validé
                </PillButton>
              )}
              
              <PillButton
                variant="secondary"
                onClick={handleSave}
                disabled={saving}
                icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              >
                Enregistrer
              </PillButton>
              
              {getContentStatus(content) !== 'published' && content.platform !== 'whatsapp' && (
                <div className="relative group">
                  <PillButton
                    type="button"
                    variant="primary"
                    onClick={handlePublish}
                    disabled={publishing || publishBlocked}
                    icon={publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    title={publishBlocked ? publishBlockedReason : ''}
                    data-testid="publish-now-button"
                  >
                    Publier maintenant
                  </PillButton>
                  {/* P0 FIX: Show blocking reason */}
                  {publishBlocked && (
                    <div className="absolute bottom-full left-0 mb-2 p-2 bg-red-100 border border-red-300 rounded-lg text-xs text-red-700 whitespace-nowrap z-50">
                      ⚠️ {publishBlockedReason}
                      {!isXConnected && (
                        <div className="mt-1 text-red-600 font-medium">
                          → Allez dans Paramètres pour connecter X
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Duplicate Modal */}
      {showDuplicateModal && (
        <DuplicateModal
          content={content}
          project={project}
          onClose={() => setShowDuplicateModal(false)}
          onDuplicate={handleDuplicateSuccess}
        />
      )}
    </>
  );
}

/**
 * Content Card Component with status distinction
 */
function ContentCard({ content, onClick }) {
  // P1-2 FIX: Guard against null/undefined content
  if (!content) {
    console.warn('[ContentCard] Received null content');
    return null;
  }
  
  const Icon = PLATFORM_ICONS[content.platform] || Globe;
  const contentStatus = getContentStatus(content);
  const status = STATUS_CONFIG[contentStatus] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  
  // P0 FIX CAMPAGNE X: Check if X content and get char count
  const isXContent = normalizePlatformId(content.platform) === 'x_twitter';
  const isXNative = isXContent && content.is_native;
  const textLength = content.text?.length || 0;
  // P0 FIX: Only apply X char validation to X content, NOT to Facebook
  const xCharStatus = isXContent ? (textLength > 280 ? 'error' : textLength > 260 ? 'warning' : 'ok') : 'ok';
  
  // P0.2 FIX: Show real publication date if published
  const displayDate = content.published && content.published_at 
    ? new Date(content.published_at).toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    : content.scheduled_date 
      ? new Date(content.scheduled_date).toLocaleDateString('fr-FR', { 
          day: 'numeric', 
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      : null;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full p-4 bg-white border-2 rounded-2xl text-left hover:shadow-lg transition-all group',
        contentStatus === 'published' && 'border-green-200 bg-green-50/30',
        contentStatus === 'validated' && 'border-blue-200 bg-blue-50/30',
        contentStatus === 'draft' && 'border-amber-200 bg-amber-50/30',
        contentStatus === 'failed' && 'border-red-200 bg-red-50/30',
        // P0: Highlight X content with special border when too long
        isXContent && xCharStatus === 'error' && 'border-red-300 bg-red-50/50',
        !['published', 'validated', 'draft', 'failed'].includes(contentStatus) && 'border-gray-200'
      )}
      data-testid={`content-card-${content.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-md relative',
          PLATFORM_COLORS[content.platform] || 'bg-gray-500'
        )}>
          <Icon className="w-6 h-6" />
          {/* P0: X native badge indicator */}
          {isXContent && (
            <div className={cn(
              'absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded text-[8px] font-bold shadow',
              xCharStatus === 'ok' && 'bg-gray-900 text-white',
              xCharStatus === 'warning' && 'bg-orange-500 text-white',
              xCharStatus === 'error' && 'bg-red-500 text-white'
            )}>
              {isXNative ? 'Tweet' : 'X'}
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold" style={{ color: 'hsl(var(--color-text))' }}>
                {isXContent ? (isXNative ? 'X (Tweet natif)' : 'X (Twitter)') : PLATFORM_LABELS[content.platform]}
              </span>
              {/* P0 FIX CAMPAGNE X: Show X badge with char count */}
              {isXContent && (
                <span className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 font-semibold',
                  xCharStatus === 'error'
                    ? 'bg-red-100 text-red-700 border border-red-300' 
                    : xCharStatus === 'warning'
                      ? 'bg-orange-100 text-orange-700 border border-orange-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-200'
                )}>
                  {xCharStatus === 'error' && <AlertCircle className="w-3 h-3" />}
                  {xCharStatus === 'warning' && <AlertTriangle className="w-3 h-3" />}
                  <span>{textLength}/280</span>
                </span>
              )}
            </div>
            <span className={cn(
              'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-medium',
              status.color
            )}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </span>
          </div>
          
          <p className="text-sm text-gray-700 line-clamp-2 mb-2">
            {content.text || 'Contenu sans texte'}
          </p>
          
          <div className="flex items-center gap-3">
            {displayDate && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                {content.published ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Clock className="w-3 h-3" />}
                {content.published ? 'Publié le ' : ''}{displayDate}
              </div>
            )}
            {content.cta_url && (
              <div className="flex items-center gap-1 text-xs text-indigo-500">
                <ExternalLink className="w-3 h-3" />
                CTA
              </div>
            )}
            {/* P0 FIX CAMPAGNE X: Show warning if X content is too long */}
            {isXContent && xCharStatus === 'error' && (
              <div className="flex items-center gap-1 text-xs text-red-600">
                <AlertTriangle className="w-3 h-3" />
                Trop long
              </div>
            )}
          </div>
        </div>
        
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <Eye className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </button>
  );
}

/**
 * Calendar Legend Component
 */
function CalendarLegend() {
  return (
    <div className="flex flex-wrap gap-4 mb-4 p-3 bg-gray-50 rounded-xl">
      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
        <div key={key} className="flex items-center gap-2">
          <div className={cn('w-3 h-3 rounded-full', config.dotColor)} />
          <span className="text-xs text-gray-600">{config.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Week View Component
 */
function WeekView({ contents, currentWeekStart, onSelectContent, onNavigateWeek }) {
  // P0 FIX: Safe week generation
  const days = [];
  try {
    const startDate = currentWeekStart instanceof Date ? currentWeekStart : new Date(currentWeekStart);
    if (isNaN(startDate.getTime())) {
      console.error('[WeekView] Invalid currentWeekStart:', currentWeekStart);
      return (
        <GlassCard className="p-4">
          <div className="text-center py-8 text-red-500">
            Erreur: Date de début de semaine invalide
          </div>
        </GlassCard>
      );
    }
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
  } catch (e) {
    console.error('[WeekView] Error generating days:', e);
    return (
      <GlassCard className="p-4">
        <div className="text-center py-8 text-red-500">
          Erreur de génération du calendrier
        </div>
      </GlassCard>
    );
  }
  
  // P1 FIX: Helper to get LOCAL date string (not UTC) for timezone-correct calendar display
  // This fixes the +1 day bug where posts scheduled for March 11 appear on March 12
  const getLocalDateString = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    // Use local year/month/day to avoid timezone shift
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // P1 FIX: Safe content filtering using LOCAL timezone
  const getContentsForDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    try {
      // P1 FIX: Use local date string, not UTC
      const dateStr = getLocalDateString(date);
      if (!dateStr) return [];
      
      return (contents || []).filter(c => {
        if (!c || !c.scheduled_date) return false;
        try {
          const contentDate = new Date(c.scheduled_date);
          if (isNaN(contentDate.getTime())) return false;
          // P1 FIX: Compare using local date, not UTC
          const contentDateStr = getLocalDateString(contentDate);
          return contentDateStr === dateStr;
        } catch {
          return false;
        }
      });
    } catch (e) {
      console.warn('[WeekView] getContentsForDate error:', e);
      return [];
    }
  };
  
  // P0 FIX: Safe date comparison
  const isToday = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return false;
    try {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    } catch {
      return false;
    }
  };
  
  return (
    <GlassCard className="p-4">
      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onNavigateWeek(-1)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          style={{ color: 'hsl(var(--color-text))' }}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--color-text))' }}>
          Semaine du {days[0] ? days[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '...'}
        </h2>
        <button
          onClick={() => onNavigateWeek(1)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          style={{ color: 'hsl(var(--color-text))' }}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((date, index) => {
          // P0 FIX: Safe date handling in week view
          if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return <div key={index} className="min-h-[200px] p-2 rounded-xl border bg-gray-100" />;
          }
          
          const dayContents = getContentsForDate(date);
          const dayName = date.toLocaleDateString('fr-FR', { weekday: 'short' });
          const dayNum = date.getDate();
          
          return (
            <div 
              key={index}
              className={cn(
                'min-h-[200px] p-2 rounded-xl border transition-colors',
                isToday(date) ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800' : 'bg-[hsl(var(--color-surface))] border-[hsl(var(--color-border))]'
              )}
            >
              <div className={cn(
                'text-center mb-2 pb-2 border-b',
                isToday(date) ? 'border-indigo-200' : 'border-gray-100'
              )}>
                <div className="text-xs uppercase" style={{ color: 'hsl(var(--color-text-muted))' }}>{dayName}</div>
                <div className={cn(
                  'text-lg font-bold',
                  isToday(date) ? 'text-indigo-600' : 'text-[hsl(var(--color-text))]'
                )}>
                  {dayNum}
                </div>
              </div>
              
              <div className="space-y-1">
                {dayContents.slice(0, 4).map((content) => {
                  const Icon = PLATFORM_ICONS[content.platform] || Globe;
                  const contentStatus = getContentStatus(content);
                  const statusConfig = STATUS_CONFIG[contentStatus] || STATUS_CONFIG.draft;
                  
                  return (
                    <button
                      key={content.id}
                      onClick={() => onSelectContent(content)}
                      className={cn(
                        'w-full p-1.5 rounded-lg text-left text-xs transition-all hover:shadow-md flex items-center gap-1.5',
                        contentStatus === 'published' && 'bg-green-100',
                        contentStatus === 'validated' && 'bg-blue-100',
                        contentStatus === 'draft' && 'bg-amber-100',
                        !['published', 'validated', 'draft'].includes(contentStatus) && 'bg-gray-100'
                      )}
                    >
                      <div className={cn(
                        'w-5 h-5 rounded flex items-center justify-center text-white flex-shrink-0',
                        PLATFORM_COLORS[content.platform] || 'bg-gray-500'
                      )}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <span className="truncate text-gray-700">
                        {content.text?.substring(0, 20) || 'Sans texte'}...
                      </span>
                    </button>
                  );
                })}
                {dayContents.length > 4 && (
                  <div className="text-xs text-gray-400 text-center">
                    +{dayContents.length - 4} autres
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

/**
 * Calendar Step Content
 */
function CalendarStepContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { completeStep } = useProjectSteps();
  
  const [contents, setContents] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    return monday;
  });
  const [selectedDate, setSelectedDate] = useState(() => new Date()); // P0 UX: Default to today
  const [selectedContent, setSelectedContent] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'week', or 'list'
  const [filterStatus, setFilterStatus] = useState('all');
  const [deletingAll, setDeletingAll] = useState(false);
  const [automationEnabled, setAutomationEnabled] = useState(true);
  const [projectHasContent, setProjectHasContent] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  
  // Auto-validation states
  const [autoValidating, setAutoValidating] = useState(false);
  const [showValidationConfirm, setShowValidationConfirm] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  // iter 253 — true once no draft content remains anywhere ahead (weekly
  // validation progression has drained all weeks).
  const [allValidated, setAllValidated] = useState(false);
  // iter 255 — one-click long-running period validation (>=30 days) job state.
  const [periodJob, setPeriodJob] = useState(null);
  const periodPollRef = useRef(null);
  
  // iter 253 P0 — weekly validation must target the week the user is actually
  // LOOKING AT: in week view that is `currentWeekStart`, otherwise the
  // selected day's week.
  const computeWeekRange = () => {
    const base = (viewMode === 'week' ? currentWeekStart : selectedDate) || new Date();
    const d = new Date(base);
    d.setHours(0, 0, 0, 0);
    const dow = d.getDay(); // 0 = Sunday
    const monday = new Date(d);
    monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (dt) => {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { week_start: fmt(monday), week_end: fmt(sunday) };
  };

  // iter 255 — one-click validation of AT LEAST 30 days. Starts a background
  // job and polls its progress; no repeated manual clicks.
  const fmtLocalDate = (dt) => {
    const d = new Date(dt);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const pollPeriodStatus = (jobId) => {
    if (periodPollRef.current) clearInterval(periodPollRef.current);
    periodPollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/projects/${projectId}/calendar/validate-period/status`, {
          params: { job_id: jobId },
        });
        const job = res.data;
        setPeriodJob(job);
        if (job.state && job.state !== 'running') {
          clearInterval(periodPollRef.current);
          periodPollRef.current = null;
          setAutoValidating(false);
          if (job.state === 'success') {
            if ((job.total || 0) === 0) {
              toast.info('Aucun brouillon à valider sur cette période.');
            } else {
              toast.success(`✅ ${job.validated || 0} contenus validés${job.rejected ? `, ${job.rejected} rejetés` : ''}${job.errors_count ? `, ${job.errors_count} en erreur` : ''}.`);
            }
            await loadData();
            await advanceToNextDraftWeek(job.window_end);
          } else {
            toast.error(`Validation échouée : ${job.message || 'erreur inconnue'}`);
          }
          // keep the final report visible briefly, then clear
          setTimeout(() => setPeriodJob(null), 6000);
        }
      } catch (e) {
        console.error('poll period status failed', e);
        clearInterval(periodPollRef.current);
        periodPollRef.current = null;
        setAutoValidating(false);
      }
    }, 1500);
  };

  const handleValidatePeriod = async () => {
    setShowValidationConfirm(false);
    setAllValidated(false);
    setAutoValidating(true);
    // start from the Monday of the week currently in focus
    const start = fmtLocalDate((viewMode === 'week' ? currentWeekStart : selectedDate) || new Date());
    try {
      const res = await apiClient.post(`/projects/${projectId}/calendar/validate-period`, {
        start_date: start, days: 30,
      });
      const job = res.data;
      setPeriodJob(job);
      if (job.state && job.state !== 'running') {
        // already finished (e.g. nothing to validate)
        setAutoValidating(false);
        if ((job.total || 0) === 0) {
          toast.info('Aucun brouillon à valider sur cette période.');
          await loadData();
          await advanceToNextDraftWeek(job.window_end);
        }
        setTimeout(() => setPeriodJob(null), 4000);
        return;
      }
      pollPeriodStatus(job.job_id);
    } catch (e) {
      console.error('start period validation failed', e);
      toast.error('Impossible de démarrer la validation.');
      setAutoValidating(false);
    }
  };

  // iter 255 — recover an in-flight job after a browser refresh / remount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/projects/${projectId}/calendar/validate-period/status`);
        if (!cancelled && res.data && res.data.state === 'running' && res.data.job_id) {
          setPeriodJob(res.data);
          setAutoValidating(true);
          pollPeriodStatus(res.data.job_id);
        }
      } catch { /* no active job */ }
    })();
    return () => {
      cancelled = true;
      if (periodPollRef.current) clearInterval(periodPollRef.current);
    };
  }, [projectId]);


  // iter 253 — after validating a week, jump to the next future week that
  // still contains draft content; if none remains, flag "all validated".
  const advanceToNextDraftWeek = async (validatedWeekEnd) => {
    const parseLocal = (iso) => {
      const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d);
    };
    const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    const mondayOf = (dt) => {
      const d = new Date(dt); d.setHours(0, 0, 0, 0);
      const dow = d.getDay();
      d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
      return d;
    };
    const searchStart = parseLocal(validatedWeekEnd);
    searchStart.setDate(searchStart.getDate() + 1); // day after the validated week
    const searchEnd = new Date(searchStart);
    searchEnd.setDate(searchEnd.getDate() + 180);
    try {
      const res = await apiClient.get(`/projects/${projectId}/contents`, {
        params: { start_date: fmt(searchStart), end_date: fmt(searchEnd) }
      });
      const drafts = (res.data || []).filter(c => getContentStatus(c) === 'draft');
      if (drafts.length === 0) {
        setAllValidated(true);
        return false;
      }
      drafts.sort((a, b) => parseLocal(a.scheduled_date) - parseLocal(b.scheduled_date));
      const target = parseLocal(drafts[0].scheduled_date);
      setAllValidated(false);
      setCurrentWeekStart(mondayOf(target));
      setSelectedDate(target);
      setCurrentMonth(new Date(target.getFullYear(), target.getMonth(), 1));
      return true;
    } catch (e) {
      console.error('advanceToNextDraftWeek failed', e);
      return false;
    }
  };

  const handleAutoValidateWeek = async () => {
    setAutoValidating(true);
    setShowValidationConfirm(false);
    setValidationResult(null);
    setAllValidated(false);

    const { week_start, week_end } = computeWeekRange();
    const requestStartTime = performance.now();
    const requestId = `avw_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`[AUTO-VALIDATE-WEEK:${requestId}] project=${projectId} ${week_start} → ${week_end}`);

    try {
      // Bounded by backend (max 20 items) so 60s is plenty; keep 90s as safety.
      const VALIDATION_TIMEOUT_MS = 90 * 1000;
      const response = await apiClient.post(
        `/projects/${projectId}/calendar/auto-validate-week`,
        { week_start, week_end, fast: true },
        {
          timeout: VALIDATION_TIMEOUT_MS,
          headers: { 'X-Request-Id': requestId, 'X-Operation': 'auto-validate-week' },
        },
      );

      const durationMs = Math.round(performance.now() - requestStartTime);
      const result = response.data || {};
      console.log(`[AUTO-VALIDATE-WEEK:${requestId}] processed=${result.processed} validated=${result.validated} failed=${result.failed} remaining=${result.remaining} duration_ms=${durationMs}`);

      if (result.success === true) {
        // iter 253 — only surface the report modal when NOTHING was processed
        // (the user needs to know WHY). On the happy path the success toast +
        // automatic week advance are enough, so we keep the flow seamless.
        if ((result.processed || 0) === 0) {
          setValidationResult(result);
        }
        const baseMsg = `Validation de la semaine terminée : ${result.validated || 0} contenus validés, ${result.failed || 0} en échec.`;
        const remainingMsg = (result.remaining || 0) > 0
          ? `Il reste ${result.remaining} contenus à valider cette semaine. Relancez la validation.`
          : null;
        toast.success(
          <div>
            <strong>Validation hebdomadaire terminée</strong>
            <div className="text-sm mt-1">{baseMsg}</div>
            {remainingMsg && <div className="text-xs text-amber-700 mt-1">{remainingMsg}</div>}
            <div className="text-xs text-gray-400 mt-1">
              Durée: {durationMs > 1000 ? `${(durationMs / 1000).toFixed(1)}s` : `${durationMs}ms`}
            </div>
          </div>,
          { duration: 7000 },
        );
        await loadData();

        // iter 253 — auto-advance to the next week needing validation.
        // If the current week still has leftovers (backend caps per call),
        // stay so the user can re-run; otherwise jump forward.
        if ((result.remaining || 0) > 0) {
          // keep the user on this week to drain the remaining drafts
        } else {
          const advanced = await advanceToNextDraftWeek(week_end);
          if (advanced) {
            toast.info('Semaine suivante à valider sélectionnée.');
          } else {
            toast.success('🎉 Tous les contenus ont été validés !');
          }
        }
      } else {
        const errorMsg = result.error || result.message || 'La validation de la semaine a échoué';
        toast.error(errorMsg);
        setValidationResult({ ...result, _error: true });
      }
    } catch (error) {
      const durationMs = Math.round(performance.now() - requestStartTime);
      const status = error?.response?.status;
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout') || status === 504 || status === 502;
      console.error(`[AUTO-VALIDATE-WEEK:${requestId}] FAILED ${status || error.code} after ${durationMs}ms`);

      let errorMessage = error?.response?.data?.detail || error?.response?.data?.message || error.message || 'Erreur inconnue';
      if (isTimeout) {
        errorMessage = 'La validation de la semaine a été interrompue. Relancez pour continuer sur les brouillons restants.';
      }
      toast.error(
        <div>
          <strong>{isTimeout ? 'Délai dépassé' : 'Erreur'}</strong>
          <div className="text-sm mt-1">{errorMessage}</div>
        </div>,
        { duration: 8000 },
      );
    } finally {
      setAutoValidating(false);
    }
  };

  // Auto-validate 30 days handler (LEGACY — kept for back-compat; no UI entry point)
  // P0 iter 133: the 30-day button was replaced by the weekly one.
  // eslint-disable-next-line no-unused-vars
  const handleAutoValidate30Days = async () => {
    setAutoValidating(true);
    setShowValidationConfirm(false);
    setValidationResult(null); // P0 FIX: Clear previous result
    
    // P1 FIX: Detailed timing logs
    const requestStartTime = performance.now();
    const requestId = `av_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.log(`[AUTO-VALIDATE:${requestId}] ========== REQUEST START ==========`);
    console.log(`[AUTO-VALIDATE:${requestId}] project_id=${projectId}`);
    console.log(`[AUTO-VALIDATE:${requestId}] start_time=${new Date().toISOString()}`);
    
    try {
      // P1 FIX: Extended timeout for long batch operations (5 minutes)
      // Default axios timeout is 30s which is too short for 100+ content validation
      const VALIDATION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
      
      console.log(`[AUTO-VALIDATE:${requestId}] Sending POST with timeout=${VALIDATION_TIMEOUT_MS}ms`);
      
      const response = await apiClient.post(
        `/projects/${projectId}/calendar/auto-validate-next-30-days`,
        {}, // Empty body - required for POST
        { 
          params: { days: 30 },
          // P1 FIX: Extended timeout for batch LLM operations
          timeout: VALIDATION_TIMEOUT_MS,
          // P1 FIX: Add request tracking headers
          headers: {
            'X-Request-Id': requestId,
            'X-Operation': 'auto-validate-batch'
          }
        }
      );
      
      const requestEndTime = performance.now();
      const durationMs = Math.round(requestEndTime - requestStartTime);
      
      console.log(`[AUTO-VALIDATE:${requestId}] ========== REQUEST COMPLETE ==========`);
      console.log(`[AUTO-VALIDATE:${requestId}] status=${response.status}`);
      console.log(`[AUTO-VALIDATE:${requestId}] duration_ms=${durationMs}`);
      console.log(`[AUTO-VALIDATE:${requestId}] end_time=${new Date().toISOString()}`);
      
      // P0 FIX: Verify response is valid before processing
      if (!response || !response.data) {
        throw new Error('Réponse invalide du serveur');
      }
      
      const result = response.data;
      
      console.log(`[AUTO-VALIDATE:${requestId}] processed=${result.processed} validated=${result.validated} rejected=${result.rejected}`);
      if (result.duration_seconds) {
        console.log(`[AUTO-VALIDATE:${requestId}] backend_duration_s=${result.duration_seconds.toFixed(2)}`);
      }
      
      // P0 FIX: Verify success field explicitly
      if (result.success === true) {
        setValidationResult(result);
        
        // P0 FIX: Show appropriate message based on processed count
        if (result.processed === 0) {
          // No content was processed - show reason
          const reason = result.reason || result.message || 'Aucun contenu éligible trouvé';
          toast.info(
            <div>
              <strong>Aucun contenu à valider</strong>
              <div className="text-sm mt-1">{reason}</div>
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.success(
            <div>
              <strong>Validation terminée</strong>
              <div className="text-sm mt-1">
                {result.validated || 0} validé(s) • {result.corrected || 0} corrigé(s) • {result.rejected || 0} rejeté(s)
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Durée: {durationMs > 1000 ? `${(durationMs/1000).toFixed(1)}s` : `${durationMs}ms`}
              </div>
            </div>,
            { duration: 6000 }
          );
        }
        // Rafraîchir le calendrier
        await loadData();
      } else {
        // P0 FIX: Handle explicit failure
        const errorMsg = result.error || result.message || 'La validation a échoué';
        toast.error(errorMsg);
        setValidationResult({ ...result, _error: true });
      }
      
    } catch (error) {
      const requestEndTime = performance.now();
      const durationMs = Math.round(requestEndTime - requestStartTime);
      
      console.error(`[AUTO-VALIDATE:${requestId}] ========== REQUEST FAILED ==========`);
      console.error(`[AUTO-VALIDATE:${requestId}] duration_ms=${durationMs}`);
      console.error(`[AUTO-VALIDATE:${requestId}] error_type=${error.code || error.name || 'unknown'}`);
      console.error(`[AUTO-VALIDATE:${requestId}] error_message=${error.message}`);
      
      // P1 FIX: Detect timeout errors specifically
      let errorMessage = 'Erreur lors de la validation automatique';
      let isTimeoutError = false;
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        isTimeoutError = true;
        errorMessage = `La validation a dépassé le délai (${(durationMs/1000).toFixed(0)}s). Le batch contient peut-être trop de contenus. Réessayez avec moins de contenus ou contactez le support.`;
        console.error(`[AUTO-VALIDATE:${requestId}] TIMEOUT_DETECTED after ${durationMs}ms`);
      } else if (error.response) {
        // HTTP error response
        const status = error.response.status;
        const data = error.response.data;
        
        console.error(`[AUTO-VALIDATE:${requestId}] http_status=${status}`);
        
        if (status === 405) {
          errorMessage = 'Erreur de méthode HTTP (405). Contactez le support.';
          console.error(`[AUTO-VALIDATE:${requestId}] 405 Method Not Allowed - check API route configuration`);
        } else if (status === 504 || status === 502) {
          errorMessage = `Timeout serveur/proxy (${status}). Le batch est peut-être trop volumineux.`;
          isTimeoutError = true;
        } else if (data?.detail) {
          errorMessage = data.detail;
        } else if (data?.message) {
          errorMessage = data.message;
        } else {
          errorMessage = `Erreur ${status}: ${error.message}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(
        <div>
          <strong>{isTimeoutError ? 'Délai dépassé' : 'Erreur'}</strong>
          <div className="text-sm mt-1">{errorMessage}</div>
          {isTimeoutError && (
            <div className="text-xs text-gray-400 mt-1">
              Astuce: Essayez de valider par semaine au lieu de 30 jours
            </div>
          )}
        </div>,
        { duration: 10000 }
      );
      
      // P0 FIX: Do NOT show success modal on error
      // setValidationResult is NOT called here
    } finally {
      setAutoValidating(false);
    }
  };
  
  // Toggle automation
  const toggleAutomation = async () => {
    try {
      const newState = !automationEnabled;
      await apiClient.post(`/projects/${projectId}/automation`, { enabled: newState });
      setAutomationEnabled(newState);
      toast.success(newState ? 'Publication automatique activée' : 'Publication automatique désactivée');
    } catch (error) {
      toast.error('Erreur lors du changement');
    }
  };
  
  // iter 252 P0 — the calendar now fetches ONLY the window it renders, so a
  // large project history (>1000 docs) can never again push freshly generated
  // future content past a global API limit. All date strings are LOCAL
  // (never toISOString) to avoid the previously-fixed +1 day shift.
  const visibleWindow = React.useMemo(() => {
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    if (viewMode === 'week') {
      const start = new Date(currentWeekStart);
      const end = new Date(currentWeekStart);
      end.setDate(end.getDate() + 6);
      return { start: fmt(start), end: fmt(end) };
    }
    // month ('calendar') + list: request the FULL visible grid (Monday of the
    // week containing the 1st → Sunday of the week containing the last day) so
    // any leading/trailing adjacent-month days are always covered.
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const startDow = firstOfMonth.getDay();           // 0=Sun..6=Sat
    const leadBack = startDow === 0 ? 6 : startDow - 1; // Monday-first grid
    const gridStart = new Date(year, month, 1 - leadBack);
    const endDow = lastOfMonth.getDay();
    const trailFwd = endDow === 0 ? 0 : 7 - endDow;   // extend to Sunday
    const gridEnd = new Date(year, month, lastOfMonth.getDate() + trailFwd);
    return { start: fmt(gridStart), end: fmt(gridEnd) };
  }, [viewMode, currentMonth, currentWeekStart]);

  const loadContents = useCallback(async () => {
    try {
      const res = await apiClient.get(`/projects/${projectId}/contents`, {
        params: { start_date: visibleWindow.start, end_date: visibleWindow.end }
      });
      // P0 FIX: canonical status derivation — validation_flag is the field
      // every content-creation path persists; status is legacy/absent on some
      // documents. getContentStatus() is the single source of truth.
      const data = (res.data || []).map(content => ({
        ...content,
        status: getContentStatus(content)
      }));
      setContents(data);
    } catch (error) {
      console.error('Failed to load contents:', error);
      toast.error('Erreur lors du chargement des contenus');
    }
  }, [projectId, visibleWindow.start, visibleWindow.end]);

  const loadProjectMeta = useCallback(async () => {
    try {
      const res = await apiClient.get(`/projects/${projectId}`);
      setProject(res.data);
      setAutomationEnabled(res.data?.automation_enabled !== false);
      setProjectHasContent(Boolean(res.data?.content_generated || res.data?.status?.content_generated));
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  }, [projectId]);

  // loadData refreshes the CURRENT window + project meta (used after mutations).
  // It never resets the month/week the user is viewing.
  const loadData = useCallback(async () => {
    await Promise.all([loadProjectMeta(), loadContents()]);
  }, [loadProjectMeta, loadContents]);

  // Project meta once per project
  useEffect(() => {
    loadProjectMeta();
  }, [loadProjectMeta]);

  // Contents whenever the visible window changes (mount, month/week/view nav).
  // Only the first load shows the full-screen spinner; navigation refreshes
  // silently to avoid blanking the calendar.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!hasLoadedOnceRef.current) setLoading(true);
      await loadContents();
      if (active) {
        setLoading(false);
        hasLoadedOnceRef.current = true;
      }
    })();
    return () => { active = false; };
  }, [loadContents]);
  
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    const adjustedStartDay = startingDay === 0 ? 6 : startingDay - 1;
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };
  
  const getContentsForDate = useCallback((date) => {
    // P1-2 FIX: Guard against null/undefined date
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.warn('[CalendarStep] getContentsForDate called with invalid date:', date);
      return [];
    }
    
    try {
      // P1 FIX: Use local date string, not UTC (fixes +1 day bug)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      return contents.filter(c => {
        if (!c || !c.scheduled_date) return false;
        try {
          const contentDate = new Date(c.scheduled_date);
          if (isNaN(contentDate.getTime())) return false;
          // P1 FIX: Compare using local date, not UTC
          const cYear = contentDate.getFullYear();
          const cMonth = String(contentDate.getMonth() + 1).padStart(2, '0');
          const cDay = String(contentDate.getDate()).padStart(2, '0');
          const contentDateStr = `${cYear}-${cMonth}-${cDay}`;
          return contentDateStr === dateStr;
        } catch (e) {
          console.warn('[CalendarStep] Invalid content date:', c.scheduled_date);
          return false;
        }
      });
    } catch (e) {
      console.error('[CalendarStep] getContentsForDate error:', e);
      return [];
    }
  }, [contents]);
  
  // P0 FIX: Safe filtered contents with guard
  const filteredContents = React.useMemo(() => {
    if (!Array.isArray(contents)) {
      console.warn('[CalendarStep] contents is not an array:', typeof contents);
      return [];
    }
    return contents.filter(c => {
      if (!c) return false;
      if (filterStatus === 'all') return true;
      return getContentStatus(c) === filterStatus;
    });
  }, [contents, filterStatus]);
  
  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
    setAllValidated(false);
  };
  
  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeekStart);
    newWeek.setDate(newWeek.getDate() + (direction * 7));
    setCurrentWeekStart(newWeek);
    setSelectedDate(newWeek);
    setAllValidated(false);
  };
  
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  
  const handleSaveContent = async (updatedContent) => {
    await apiClient.put(`/contents/${updatedContent.id}`, {
      text: updatedContent.text,
      scheduled_date: updatedContent.scheduled_date,
      hashtags: updatedContent.hashtags,
      image_url: updatedContent.image_url,
      cta_url: updatedContent.cta_url,
      cta_text: updatedContent.cta_text,
      validation_flag: updatedContent.validation_flag,
      status: updatedContent.status
    });
    loadData();
  };
  
  const handleDeleteContent = async (content) => {
    await apiClient.delete(`/contents/${content.id}`);
    loadData();
  };
  
  const handleDeleteAllFuture = async () => {
    const futureCount = contents.filter(c => !c.published).length;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${futureCount} publication(s) non publiée(s) ?\n\nLes contenus déjà publiés seront conservés.`)) {
      return;
    }
    
    setDeletingAll(true);
    try {
      const response = await apiClient.delete(`/projects/${projectId}/contents`);
      if (response.data?.success) {
        toast.success(response.data.message || 'Publications supprimées');
        loadData();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingAll(false);
    }
  };
  
  const handlePublishContent = async (content) => {
    // P0 FIX CAMPAGNE X: Validate before sending request
    const isXContent = normalizePlatformId(content.platform) === 'x_twitter';
    
    if (isXContent) {
      // P0 FIX: EXACT same logic as backend
      // Rule: 
      //   - If platform IS X → use content.text (X native content)
      //   - If platform is NOT X but publishing TO X → use variants.x_twitter.text (duplicated content)
      // This prevents publishing wrong text (e.g., long LinkedIn text instead of X variant)
      
      const isXNative = normalizePlatformId(content.platform) === 'x_twitter';
      let textToPublish = '';
      
      if (isXNative) {
        // X native content → content.text IS the tweet
        textToPublish = content.text || '';
      } else {
        // Non-X content being published to X → MUST have X variant
        // Do NOT fallback to content.text (could be long LinkedIn/FB text)
        textToPublish = content.variants?.x_twitter?.text || '';
        
        if (!textToPublish.trim()) {
          throw new Error('❌ Variante X manquante. Créez une variante X avant de publier vers X.');
        }
      }
      
      // Client-side validation to avoid unnecessary network requests
      if (!textToPublish.trim()) {
        throw new Error('❌ Le contenu du tweet est vide. Ajoutez du texte avant de publier.');
      }
      if (textToPublish.length > 280) {
        throw new Error(`❌ Le tweet dépasse 280 caractères (${textToPublish.length}). Raccourcissez le contenu.`);
      }
      
      // Log for debugging (visible in browser console)
      console.info(`[PUBLISH_X] content_id=${content.id} platform=${content.platform} is_native=${isXNative} text_len=${textToPublish.length}`);
    }
    
    // P0 FIX: Make POST request to publish endpoint with extended timeout
    // Facebook/Instagram publish can take >30s due to image upload to Meta servers
    // P0 DEBUG: Log content image_url before publish for diagnosis
    console.info(`[PUBLISH] POST /contents/${content.id}/publish platform=${content.platform}`, {
      content_id: content.id,
      platform: content.platform,
      image_url: content.image_url || 'NONE',
      image_url_length: content.image_url?.length || 0,
      selected_image_url: content.selected_image_url || 'NONE',
      variants_x_image: content.variants?.x_twitter?.image_url || content.variants?.x?.image_url || 'NONE'
    });
    
    // P0 FIX: Extended timeout (90s) for publish requests - Facebook can be slow with images
    const response = await apiClient.post(`/contents/${content.id}/publish`, {
      platform: content.platform || 'x_twitter',
      mode: 'now'
    }, {
      timeout: 90000  // 90 seconds for publish operations
    });
    
    const data = response.data;
    
    // Log result
    console.info(`[PUBLISH] Result: ok=${data.ok} error=${data.error || 'none'} tweet_id=${data.tweet_id || 'N/A'}`);
    
    // Check for structured error responses
    if (data.ok === false || data.success === false) {
      // Handle specific error cases
      if (data.error === 'MISSING_INTEGRATION') {
        throw new Error(`❌ ${data.integration === 'x_twitter' ? 'X (Twitter)' : data.integration} n'est pas connecté. Allez dans Paramètres > Intégrations.`);
      }
      if (data.error === 'EMPTY_CONTENT') {
        throw new Error('❌ Le contenu du tweet est vide. Ajoutez du texte avant de publier.');
      }
      if (data.error === 'MISSING_VARIANT_X') {
        throw new Error('❌ Variante X manquante. Créez une variante X avant de publier ce contenu vers X.');
      }
      // P0 FIX v2: Handle X_MEDIA_UPLOAD_FORBIDDEN with specific action codes
      if (data.error_code === 'X_MEDIA_UPLOAD_FORBIDDEN') {
        let actionHint = '';
        switch (data.action_required) {
          case 'CONFIGURE_OAUTH1':
            actionHint = ' Configurez le fallback OAuth 1.0a dans Paramètres > X.';
            break;
          case 'OAUTH1_INVALID':
            actionHint = ' Vos credentials OAuth 1.0a sont invalides. Régénérez-les dans le X Developer Portal.';
            break;
          case 'OAUTH1_MISSING':
            actionHint = ' Credentials OAuth 1.0a incomplets.';
            break;
          default:
            actionHint = ' Configurez OAuth 1.0a ou upgradez votre plan X.';
        }
        throw new Error(`❌ L'API X bloque l'upload d'image.${actionHint}`);
      }
      if (data.error === 'MEDIA_UPLOAD_FAILED' || data.error_code === 'MEDIA_UPLOAD_FAILED') {
        // P0 FIX v3: Publication was BLOCKED because media upload failed
        // Build detailed error message
        let errorDetail = '';
        if (data.media_error_code === 'TOKEN_ERROR') {
          errorDetail = 'Erreur authentification X - reconnectez votre compte dans Paramètres > X.';
        } else if (data.media_error_code === 'FORBIDDEN') {
          errorDetail = 'Accès refusé par X - vérifiez vos permissions OAuth 1.0a.';
        } else if (data.media_error_code === 'DOWNLOAD_FAILED') {
          errorDetail = `Échec téléchargement de l'image (HTTP ${data.media_error_http_status}).`;
        } else if (data.media_error_code === 'IMAGE_TOO_LARGE') {
          errorDetail = 'Image trop volumineuse (max 5MB).';
        } else if (data.media_error_code === 'MISSING_CREDENTIALS') {
          errorDetail = 'Credentials OAuth 1.0a manquants - reconnectez votre compte X.';
        } else {
          errorDetail = data.media_error_message || 'Erreur inconnue lors de l\'upload.';
        }
        
        // Log detailed info for debugging
        console.error('[PUBLISH] MEDIA_UPLOAD_FAILED - Publication blocked:', {
          tweet_created: data.tweet_created,
          media_error_code: data.media_error_code,
          media_error_http_status: data.media_error_http_status,
          media_auth_type_used: data.media_auth_type_used,
          image_url_used: data.image_url_used,
          selected_image_url: data.selected_image_url,
          image_source: data.image_source,
          source_document_used: data.source_document_used,
          oauth1_config_present: data.oauth1_config_present,
          stage: data.stage,
          action_required: data.action_required
        });
        
        // P0 FIX: Log diagnostic data for RCA
        if (data.credentials_comparison) {
          console.error('[PUBLISH] 🔍 CREDENTIALS_COMPARISON (compare avec /api/integrations/x/oauth1/debug):', data.credentials_comparison);
        }
        if (data.signature_info) {
          console.error('[PUBLISH] 🔍 SIGNATURE_INFO:', data.signature_info);
        }
        if (data.auth_debug) {
          console.error('[PUBLISH] 🔍 AUTH_DEBUG:', data.auth_debug);
        }
        if (data.x_diagnosis) {
          console.error('[PUBLISH] 🔍 X_DIAGNOSIS:', data.x_diagnosis);
        }
        
        throw new Error(`❌ Publication annulée: l'image n'a pas pu être uploadée. ${errorDetail}`);
      }
      
      // P0 FIX: Handle MEDIA_NOT_ATTACHED - tweet posted but image not joined
      if (data.error_code === 'MEDIA_NOT_ATTACHED') {
        // Log detailed info for debugging
        console.warn('[PUBLISH] MEDIA_NOT_ATTACHED:', {
          tweet_id: data.tweet_id,
          image_requested: data.image_requested,
          media_upload_attempted: data.media_upload_attempted,
          media_uploaded: data.media_uploaded,
          media_upload_error: data.media_upload_error,
          image_url_used: data.image_url_used,
          image_source: data.image_source
        });
        
        // Build detailed error message
        let errorDetail = '';
        if (data.media_upload_error) {
          const uploadErr = data.media_upload_error;
          if (uploadErr.error_code === 'DOWNLOAD_FAILED') {
            errorDetail = ` (Échec téléchargement: HTTP ${uploadErr.http_status})`;
          } else if (uploadErr.error_code === 'TOKEN_ERROR') {
            errorDetail = ' (Erreur authentification X - reconnectez votre compte)';
          } else if (uploadErr.error_code === 'FORBIDDEN') {
            errorDetail = ' (Accès refusé par X - vérifiez vos permissions)';
          } else if (uploadErr.error_code === 'IMAGE_TOO_LARGE') {
            errorDetail = ' (Image trop volumineuse, max 5MB)';
          } else if (uploadErr.error_code === 'TIMEOUT') {
            errorDetail = ' (Timeout - réessayez)';
          } else {
            errorDetail = ` (${uploadErr.error_code}: ${uploadErr.message?.substring(0, 50) || 'erreur inconnue'})`;
          }
        }
        
        // This is a partial success - tweet was posted but without image
        throw new Error(`⚠️ Tweet publié (ID: ${data.tweet_id}) mais l'image n'a pas été jointe${errorDetail}. X affichera uniquement la card du lien.`);
      }
      if (data.error === 'UNSUPPORTED_FORMAT') {
        throw new Error('❌ Format d\'image non supporté. Utilisez JPEG ou PNG (pas WEBP).');
      }
      if (data.error === 'IMAGE_TOO_LARGE') {
        throw new Error('❌ Image trop volumineuse (max 5 Mo). Réduisez la taille de l\'image.');
      }
      if (data.error === 'TOKEN_EXPIRED') {
        throw new Error('❌ Session expirée. Reconnectez votre compte X dans les paramètres.');
      }
      if (data.error === 'RATE_LIMIT') {
        throw new Error('⏳ Limite de tweets atteinte. Réessayez dans quelques minutes.');
      }
      if (data.error === 'CONTENT_TOO_LONG') {
        throw new Error(`❌ Le tweet dépasse 280 caractères (${data.char_count}). Raccourcissez le contenu.`);
      }
      if (data.error === 'MISSING_PERMISSIONS') {
        throw new Error('❌ Permissions insuffisantes. Reconnectez X avec les bonnes autorisations.');
      }
      // Generic error — iter 247 P0: preserve the full structured error
      // (never collapse to a blind default when the backend provided one).
      const genericMsg = data.message || data.error?.message || data.detail?.message || 'Publication échouée';
      const genericErr = new Error(genericMsg);
      genericErr.error_code = data.error_code;
      genericErr.stage = data.stage;
      genericErr.trace_id = data.trace_id;
      genericErr.pinterest_status = data.pinterest_status;
      genericErr.pinterest_message = data.pinterest_message;
      throw genericErr;
    }
    
    loadData();
    return data;
  };
  
  const handleContinue = () => {
    completeStep('calendar');
    navigate(`/projects/${projectId}/setup/results`);
  };
  
  // P0 FIX: Safe days array with guard
  const days = React.useMemo(() => {
    try {
      const result = getDaysInMonth();
      return Array.isArray(result) ? result : [];
    } catch (e) {
      console.error('[CalendarStep] Error getting days in month:', e);
      return [];
    }
  }, [currentMonth]);
  
  // P1-2 FIX: Safe selectedDayContents with guard
  const selectedDayContents = React.useMemo(() => {
    if (!selectedDate) return [];
    try {
      const result = getContentsForDate(selectedDate);
      return Array.isArray(result) ? result.filter(Boolean) : [];
    } catch (e) {
      console.error('[CalendarStep] Error getting contents for selected date:', e);
      return [];
    }
  }, [selectedDate, getContentsForDate]);
  
  const stats = {
    total: contents.length,
    draft: contents.filter(c => getContentStatus(c) === 'draft').length,
    validated: contents.filter(c => getContentStatus(c) === 'validated').length,
    scheduled: contents.filter(c => getContentStatus(c) === 'scheduled').length,
    published: contents.filter(c => getContentStatus(c) === 'published').length
  };
  
  // P0 DASHBOARD: Calculate channel stats
  const channelStats = contents.reduce((acc, c) => {
    const platform = normalizePlatformId(c.platform);
    acc[platform] = (acc[platform] || 0) + 1;
    return acc;
  }, {});
  
  // Sort channels by count descending
  const sortedChannels = Object.entries(channelStats)
    .sort((a, b) => b[1] - a[1])
    .map(([platform, count]) => ({
      platform,
      count,
      label: PLATFORM_LABELS[platform] || platform,
      Icon: PLATFORM_ICONS[platform] || Globe,
      color: PLATFORM_COLORS[platform] || 'bg-gray-500'
    }));
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  if (contents.length === 0 && !projectHasContent) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="w-20 h-20 rounded-3xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
          <CalendarIcon className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-xl font-bold mb-3" style={{ color: 'hsl(var(--color-text))' }}>
          Aucun contenu généré
        </h2>
        <p className="text-gray-500 mb-6">
          Générez d&apos;abord du contenu pour le voir apparaître dans votre calendrier éditorial.
        </p>
        <PillButton
          variant="primary"
          onClick={() => navigate(`/projects/${projectId}/setup/generate`)}
          icon={<RefreshCw className="w-5 h-5" />}
        >
          Générer du contenu
        </PillButton>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto">
      <SectionHeader
        title="Calendrier éditorial"
        subtitle={`${stats.total} publication${stats.total > 1 ? 's' : ''} sur cette période`}
      />
      
      {/* Stats Bar */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <GlassCard className="p-4 text-center border-2 border-gray-200">
          <div className="text-2xl font-bold" style={{ color: 'hsl(var(--color-text))' }}>{stats.total}</div>
          <div className="text-xs text-gray-500">Total</div>
        </GlassCard>
        <GlassCard className="p-4 text-center border-2 border-amber-200 bg-amber-50/50">
          <div className="text-2xl font-bold text-amber-600">{stats.draft}</div>
          <div className="text-xs text-amber-600">Brouillons</div>
        </GlassCard>
        <GlassCard className="p-4 text-center border-2 border-blue-200 bg-blue-50/50">
          <div className="text-2xl font-bold text-blue-600">{stats.validated}</div>
          <div className="text-xs text-blue-600">Validés</div>
        </GlassCard>
        <GlassCard className="p-4 text-center border-2 border-purple-200 bg-purple-50/50">
          <div className="text-2xl font-bold text-purple-600">{stats.scheduled}</div>
          <div className="text-xs text-purple-600">Planifiés</div>
        </GlassCard>
        <GlassCard className="p-4 text-center border-2 border-green-200 bg-green-50/50">
          <div className="text-2xl font-bold text-green-600">{stats.published}</div>
          <div className="text-xs text-green-600">Publiés</div>
        </GlassCard>
      </div>
      
      {/* P0 DASHBOARD: Channel Stats Bar */}
      {sortedChannels.length > 0 && (
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-xs text-gray-500 font-medium">Par canal:</span>
          {sortedChannels.map(({ platform, count, label, Icon, color }) => (
            <div 
              key={platform}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border rounded-full shadow-sm"
            >
              <div className={cn(
                'w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px]',
                color
              )}>
                <Icon className="w-3 h-3" />
              </div>
              <span className="text-xs font-medium" style={{ color: 'hsl(var(--color-text))' }}>
                {platform === 'x_twitter' ? 'X' : label}
              </span>
              <span className="text-xs font-bold text-indigo-600">{count}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* View Toggle + Filter + Continue Button */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('calendar')}
            data-testid="view-mode-month"
            className={cn(
              'px-4 py-2 rounded-xl text-sm font-medium transition-colors',
              viewMode === 'calendar' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-[hsl(var(--color-surface-2))] text-[hsl(var(--color-text-2))] hover:bg-[hsl(var(--color-surface-3))]'
            )}
          >
            <CalendarIcon className="w-4 h-4 inline mr-2" />
            Mois
          </button>
          <button
            onClick={() => setViewMode('week')}
            data-testid="view-mode-week"
            className={cn(
              'px-4 py-2 rounded-xl text-sm font-medium transition-colors',
              viewMode === 'week' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-[hsl(var(--color-surface-2))] text-[hsl(var(--color-text-2))] hover:bg-[hsl(var(--color-surface-3))]'
            )}
          >
            <CalendarDays className="w-4 h-4 inline mr-2" />
            Semaine
          </button>
          <button
            onClick={() => setViewMode('list')}
            data-testid="view-mode-list"
            className={cn(
              'px-4 py-2 rounded-xl text-sm font-medium transition-colors',
              viewMode === 'list' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-[hsl(var(--color-surface-2))] text-[hsl(var(--color-text-2))] hover:bg-[hsl(var(--color-surface-3))]'
            )}
          >
            Liste
          </button>
        </div>
        
        {viewMode === 'list' && (
          <div className="flex gap-2">
            {['all', 'draft', 'validated', 'published'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                  filterStatus === status 
                    ? 'bg-indigo-500/20 text-indigo-500' 
                    : 'bg-[hsl(var(--color-surface-2))] text-[hsl(var(--color-text-2))] hover:bg-[hsl(var(--color-surface-3))]'
                )}
              >
                {status === 'all' ? 'Tous' : STATUS_CONFIG[status]?.label || status}
              </button>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 items-center">
          {/* Automation Toggle */}
          <button
            onClick={toggleAutomation}
            className={cn(
              'px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2',
              automationEnabled 
                ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20' 
                : 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20'
            )}
            title={automationEnabled ? 'Publication automatique active' : 'Publication automatique désactivée'}
          >
            <div className={cn(
              'w-3 h-3 rounded-full',
              automationEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
            )} />
            Auto
          </button>
          
          {/* iter 255: one-click validation of AT LEAST 30 days */}
          <button
            onClick={() => setShowValidationConfirm(true)}
            disabled={autoValidating || contents.length === 0}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg"
            title="Valider automatiquement les contenus des 30 prochains jours (par lots)"
            data-testid="validate-period-btn"
          >
            {autoValidating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Valider 30 jours
          </button>
          
          {contents.length > 0 && (
            <button
              onClick={handleDeleteAllFuture}
              disabled={deletingAll}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50 flex items-center gap-2"
            >
              {deletingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Supprimer futures
            </button>
          )}
          
          <PillButton
            variant="primary"
            onClick={handleContinue}
            icon={<ArrowRight className="w-4 h-4" />}
            iconPosition="right"
          >
            Continuer vers Résultats
          </PillButton>
        </div>
      </div>
      
      <CalendarLegend />
      
      {periodJob && (periodJob.state === 'running' || periodJob.total > 0) && (
        <div
          className="mb-4 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200"
          data-testid="period-validation-progress"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {periodJob.state === 'running'
                ? <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                : <CheckCircle className="w-5 h-5 text-emerald-600" />}
              <h4 className="font-semibold text-gray-900">
                {periodJob.state === 'running' ? 'Validation des contenus en cours' : 'Validation terminée'}
              </h4>
            </div>
            <span className="text-sm font-medium text-indigo-700" data-testid="period-progress-count">
              {periodJob.processed} / {periodJob.total} traités
            </span>
          </div>
          <div className="w-full h-2 bg-white rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-indigo-500 rounded-full transition-all duration-500"
              style={{ width: `${periodJob.progress || 0}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
            <span>✅ {periodJob.validated} validés</span>
            <span>🗑️ {periodJob.rejected} rejetés</span>
            <span className={periodJob.errors_count > 0 ? 'text-red-600' : ''}>⚠️ {periodJob.errors_count} en erreur</span>
            <span className="text-gray-500">Lot {periodJob.current_batch} / {periodJob.total_batches}</span>
            <span className="text-gray-500">Période : {periodJob.window_start} → {periodJob.window_end}</span>
          </div>
        </div>
      )}
      
      {allValidated && (
        <div
          className="mb-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 flex items-center justify-between gap-4"
          data-testid="all-validated-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Tous les contenus ont été validés</h4>
              <p className="text-sm text-gray-600">Il n'y a plus aucune semaine à valider.</p>
            </div>
          </div>
          <PillButton
            variant="primary"
            onClick={handleContinue}
            icon={<ArrowRight className="w-4 h-4" />}
            iconPosition="right"
            data-testid="all-validated-continue-btn"
          >
            Continuer vers Résultats
          </PillButton>
        </div>
      )}
      
      {viewMode === 'week' ? (
        <WeekView
          contents={contents}
          currentWeekStart={currentWeekStart}
          onSelectContent={setSelectedContent}
          onNavigateWeek={navigateWeek}
        />
      ) : viewMode === 'calendar' ? (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="month-view-container">
          <div className="lg:col-span-2">
            <GlassCard className="p-4" data-testid="month-calendar-grid">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-[hsl(var(--color-surface-2))] rounded-xl transition-colors text-[hsl(var(--color-text))]"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-semibold text-[hsl(var(--color-text))] capitalize">
                  {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </h2>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-[hsl(var(--color-surface-2))] rounded-xl transition-colors text-[hsl(var(--color-text))]"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-[hsl(var(--color-text-muted))] py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {days && days.length > 0 ? days.map((date, index) => {
                  // P0 FIX: Guard against invalid date objects
                  const isValidDate = date instanceof Date && !isNaN(date.getTime());
                  const dayContents = isValidDate ? getContentsForDate(date) : [];
                  
                  // P0 FIX: Safe comparison that won't crash if selectedDate is invalid
                  let isSelected = false;
                  try {
                    if (selectedDate && isValidDate) {
                      const selectedIsValid = selectedDate instanceof Date && !isNaN(selectedDate.getTime());
                      isSelected = selectedIsValid && selectedDate.toDateString() === date.toDateString();
                    }
                  } catch (e) {
                    console.warn('[CalendarStep] Date comparison error:', e);
                  }
                  
                  return (
                    <button
                      key={index}
                      onClick={() => isValidDate && setSelectedDate(date)}
                      disabled={!isValidDate}
                      className={cn(
                        'aspect-square p-1 rounded-xl transition-all text-sm relative',
                        isValidDate && 'hover:bg-gray-100',
                        isValidDate && isToday(date) && 'bg-indigo-50 font-bold text-indigo-600',
                        isValidDate && isPast(date) && !isToday(date) && 'text-gray-400',
                        isSelected && 'ring-2 ring-indigo-500 bg-indigo-50',
                        !isValidDate && 'bg-transparent cursor-default'
                      )}
                      data-testid={isValidDate ? `calendar-day-${date.getDate()}` : `calendar-empty-${index}`}
                    >
                      {isValidDate && (
                        <>
                          <span className="block">{date.getDate()}</span>
                          {dayContents.length > 0 && (
                            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                              {dayContents.slice(0, 4).map((c, i) => {
                                const statusConfig = STATUS_CONFIG[getContentStatus(c)] || STATUS_CONFIG.draft;
                                return (
                                  <div 
                                    key={i} 
                                    className={cn('w-2 h-2 rounded-full', statusConfig.dotColor)} 
                                  />
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </button>
                  );
                }) : (
                  <div className="col-span-7 text-center py-8 text-gray-500">
                    Chargement du calendrier...
                  </div>
                )}
              </div>
            </GlassCard>
          </div>
          
          <div>
            <GlassCard className="p-4">
              <h3 className="font-semibold mb-4" style={{ color: 'hsl(var(--color-text))' }}>
                {(() => {
                  // P0 FIX: Safe date formatting to prevent crash
                  if (!selectedDate) return 'Sélectionnez une date';
                  try {
                    if (!(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
                      return 'Date invalide';
                    }
                    return selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
                  } catch (e) {
                    console.error('[CalendarStep] Date formatting error:', e);
                    return 'Erreur de date';
                  }
                })()}
              </h3>
              
              {selectedDate && selectedDayContents.length === 0 && (
                <div className="text-center py-8">
                  <CalendarIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Aucune publication prévue</p>
                </div>
              )}
              
              <div className="space-y-3">
                {selectedDayContents && selectedDayContents.length > 0 ? (
                  selectedDayContents.map((content) => (
                    content && content.id ? (
                      <ContentCard
                        key={content.id}
                        content={content}
                        onClick={() => setSelectedContent(content)}
                      />
                    ) : null
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Aucun contenu ce jour</p>
                  </div>
                )}
              </div>
            </GlassCard>
          </div>
        </div>
      ) : (
        // P0 FIX: List view with empty state guard
        <div className="space-y-3" data-testid="list-view-container">
          {filteredContents.length === 0 ? (
            <div className="text-center py-12" data-testid="list-view-empty">
              <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune publication dans cette vue</p>
              <p className="text-sm text-gray-400 mt-2">
                {filterStatus !== 'all' 
                  ? `Aucun contenu avec le statut "${STATUS_CONFIG[filterStatus]?.label || filterStatus}"`
                  : 'Générez du contenu pour commencer'}
              </p>
            </div>
          ) : (
            filteredContents.map((content) => (
              content && content.id ? (
                <ContentCard
                  key={content.id}
                  content={content}
                  onClick={() => setSelectedContent(content)}
                />
              ) : null
            ))
          )}
        </div>
      )}
      
      {selectedContent && (
        <ContentEditorModal
          content={selectedContent}
          project={project}
          onClose={() => setSelectedContent(null)}
          onSave={handleSaveContent}
          onDelete={handleDeleteContent}
          onPublish={handlePublishContent}
          onRefresh={loadData}
        />
      )}
      
      {/* Auto-Validation Confirmation Modal */}
      {showValidationConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Valider les 30 prochains jours
                </h3>
                <p className="text-sm text-gray-500">
                  {(() => {
                    const base = (viewMode === 'week' ? currentWeekStart : selectedDate) || new Date();
                    const end = new Date(base); end.setDate(end.getDate() + 29);
                    return `Du ${fmtLocalDate(base)} au ${fmtLocalDate(end)}`;
                  })()}
                </p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Important :</strong> Les contenus validés seront mis en file de publication automatique.
                Les contenus rejetés seront déplacés en corbeille.
                <strong className="block mt-2">Aucun contenu ne sera publié directement.</strong>
                <span className="block mt-2 text-xs text-amber-700">
                  Les contenus seront analysés automatiquement par lots — un seul clic suffit.
                </span>
              </p>
            </div>
            
            <div className="text-sm text-gray-600 mb-4">
              <p>Le système va analyser :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Cohérence texte / image</li>
                <li>Présence d'un appel à l'action (CTA)</li>
                <li>Longueur adaptée au canal</li>
                <li>Lien vers TTT</li>
                <li>Détection de doublons</li>
                <li>Score qualité marketing</li>
              </ul>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowValidationConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                data-testid="auto-validate-week-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={handleValidatePeriod}
                className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 flex items-center gap-2"
                data-testid="validate-period-confirm-btn"
              >
                <Sparkles className="w-4 h-4" />
                Lancer la validation
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Validation Result Modal - P0 FIX: Show reason when processed=0 */}
      {validationResult && validationResult.success === true && !validationResult._error && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                validationResult.processed > 0 ? "bg-green-100" : "bg-amber-100"
              )}>
                {validationResult.processed > 0 ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {validationResult.processed > 0 ? 'Rapport de validation' : 'Aucun contenu traité'}
                </h3>
                <p className="text-sm text-gray-500">
                  {validationResult.processed || 0} contenu(s) traité(s)
                </p>
              </div>
            </div>
            
            {/* P0 FIX: Show reason when no content processed */}
            {validationResult.processed === 0 && validationResult.reason && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Raison :</strong> {validationResult.reason}
                </p>
              </div>
            )}
            
            {/* P0 FIX: Show audit details when processed=0 */}
            {validationResult.processed === 0 && validationResult.audit && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-sm">
                <p className="font-medium text-gray-700 mb-2">Détails du diagnostic :</p>
                <ul className="space-y-1 text-gray-600">
                  <li>• Contenus dans le projet : <strong>{validationResult.audit.total_in_project || 0}</strong></li>
                  {validationResult.audit.filter_rejections && (
                    <>
                      {validationResult.audit.filter_rejections.date_in_past > 0 && (
                        <li className="text-amber-600">• Dates passées : {validationResult.audit.filter_rejections.date_in_past}</li>
                      )}
                      {validationResult.audit.filter_rejections.date_too_far_future > 0 && (
                        <li className="text-blue-600">• Au-delà de 30 jours : {validationResult.audit.filter_rejections.date_too_far_future}</li>
                      )}
                      {validationResult.audit.filter_rejections.already_validated > 0 && (
                        <li className="text-green-600">• Déjà validés : {validationResult.audit.filter_rejections.already_validated}</li>
                      )}
                      {validationResult.audit.filter_rejections.already_published > 0 && (
                        <li className="text-green-600">• Déjà publiés : {validationResult.audit.filter_rejections.already_published}</li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            )}
            
            {validationResult.processed > 0 && (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{validationResult.validated || 0}</div>
                    <div className="text-xs text-green-700">Validés</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{validationResult.corrected || 0}</div>
                    <div className="text-xs text-amber-700">Corrigés</div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{validationResult.rejected || 0}</div>
                    <div className="text-xs text-red-700">Rejetés</div>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-4 space-y-1">
                  <p>En file de publication : <strong>{validationResult.queued_for_publish || 0}</strong></p>
                  <p>En corbeille : <strong>{validationResult.queued_for_trash || 0}</strong></p>
                </div>
              </>
            )}
            
            {validationResult.duration_seconds && (
              <p className="text-xs text-gray-400 mb-4">
                Durée : {validationResult.duration_seconds.toFixed(2)}s
              </p>
            )}
            
            {validationResult.errors && validationResult.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <p className="text-sm font-medium text-red-800 mb-1">Erreurs :</p>
                <ul className="text-xs text-red-600 list-disc list-inside">
                  {validationResult.errors.slice(0, 3).map((err, i) => (
                    <li key={i}>{err.content_id}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setValidationResult(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// P0 FIX: Error Boundary to prevent white pages
class CalendarErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[CalendarStep] Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
          <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Une erreur s'est produite
          </h3>
          <p className="text-gray-500 text-center mb-4">
            {this.state.error?.message || 'Erreur inconnue dans le calendrier'}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Recharger
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function CalendarStep() {
  return (
    <GuidedProjectLayout>
      <CalendarErrorBoundary>
        <CalendarStepContent />
      </CalendarErrorBoundary>
    </GuidedProjectLayout>
  );
}
